import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { portalMeAPI, portalAuthAPI } from '../lib/portalApi';

const PortalAuthContext = createContext(null);

/**
 * B2B portal auth state — Wave-2 cookie edition.
 *
 * The auth token now lives in an httpOnly cookie (`uc_portal_token`)
 * that JavaScript cannot read. This context therefore doesn't hold
 * the token in React state; it holds the profile fetched via
 * `GET /portal/me`, which returns 401 when no valid cookie is present.
 *
 * `email` is derived from the /portal/me payload so the UI has
 * something to render in the top bar without leaking auth material.
 */
export const PortalAuthProvider = ({ children }) => {
    const [email, setEmail] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await portalMeAPI.get();
            setProfile(data);
            setEmail(data?.email || null);
            return data;
        } catch {
            // 401 → not signed in. Interceptor handles the redirect
            // on protected routes; public routes just stay unauthenticated.
            setProfile(null);
            setEmail(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    /**
     * Impersonation / OTP-verify handoff.
     *
     * Callers (PortalImpersonate, PortalLogin) receive `{token, email}`
     * from admin or OTP verify. We POST it to `/portal/auth/set-session`
     * so the portal HOST itself emits the Set-Cookie header — this
     * ensures the browser scopes the cookie to `portal.` (never leaked
     * cross-subdomain).
     */
    const setSession = async ({ token, email: e }) => {
        setLoading(true);
        await portalAuthAPI.setSession(token, e);
        return refresh();
    };

    const logout = async () => {
        try { await portalAuthAPI.logout(); } catch { /* best-effort */ }
        // Session-scoped state is UI-only, must still be cleared so the
        // impersonation banner and recently-viewed list don't leak
        // across accounts on the same tab.
        try {
            sessionStorage.removeItem('uc.portal.impersonated');
            sessionStorage.removeItem('uc.portal.recently_viewed');
        } catch { /* private-mode / quota — ignore */ }
        setEmail(null);
        setProfile(null);
    };

    return (
        <PortalAuthContext.Provider value={{ email, profile, loading, setSession, logout, refresh }}>
            {children}
        </PortalAuthContext.Provider>
    );
};

export const usePortalAuth = () => {
    const ctx = useContext(PortalAuthContext);
    if (!ctx) throw new Error('usePortalAuth must be used within PortalAuthProvider');
    return ctx;
};
